import { useState } from 'react';
import { useQuery, type QueryKey } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  Field,
  Input,
  Select,
  Spinner,
  Textarea,
  type Tone,
} from '@zt/ui';
import { api, IdempotencyKey } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { InvoiceRow } from '../lib/types';

export const invoiceTone: Record<string, Tone> = {
  draft: 'neutral',
  sending: 'amber',
  sent: 'moss',
  failed: 'rose',
  uncertain: 'amber',
  cancelled: 'neutral',
};

interface InvoiceLike {
  id: string;
  provider: string;
  status: string;
  number: string | null;
  providerUrl: string | null;
  error: string | null;
  /** BizimHesap iptal durumu (F7-25): sipariş tamamen iptal edilince. */
  providerCancel?: 'required' | 'done' | 'failed' | 'uncertain' | 'return_required' | null;
  providerCancelError?: string | null;
}

const cancelTone: Record<string, Tone> = {
  required: 'amber',
  failed: 'rose',
  uncertain: 'amber',
  return_required: 'rose',
  done: 'moss',
};

/** Fatura satırındaki işlemler: gönder, belirsiz faturayı işaretle, iptal edildi olarak işaretle. */
export function InvoiceActions({ invoice, invalidate }: { invoice: InvoiceLike; invalidate: QueryKey[] }) {
  const t = useT();
  const { can } = usePermissions();
  const [dialog, setDialog] = useState<'resolve' | 'cancel' | 'provider' | null>(null);
  const [idem] = useState(() => new IdempotencyKey());
  const [cancelIdem] = useState(() => new IdempotencyKey());
  const providerCancel = useApiMutation(
    () =>
      api.post(`/v1/admin/invoices/${invoice.id}/provider-cancel`, undefined, { idempotencyKey: cancelIdem }),
    {
      invalidate,
      success: (r: unknown) =>
        t(
          `admin.invoices.providerCancelResult.${(r as { providerCancel: 'done' | 'failed' | 'uncertain' }).providerCancel}`,
        ),
    },
  );
  const pc = invoice.providerCancel ?? null;
  const send = useApiMutation(
    () => api.post(`/v1/admin/invoices/${invoice.id}/send`, undefined, { idempotencyKey: idem }),
    {
      invalidate,
      success: (r: unknown) => t(`admin.invoices.sendResult.${(r as { status: string }).status}`),
    },
  );
  if (!can('invoices.invoice.write'))
    return pc ? <Badge tone={cancelTone[pc]}>{t(`admin.invoices.providerCancelStates.${pc}`)}</Badge> : null;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {pc && (
        <span className="inline-flex flex-wrap items-center gap-1">
          <Badge tone={cancelTone[pc]}>{t(`admin.invoices.providerCancelStates.${pc}`)}</Badge>
          {invoice.providerCancelError && pc !== 'done' && (
            <span className="text-xs text-ink-3">{invoice.providerCancelError}</span>
          )}
          {(pc === 'required' || pc === 'failed') && (
            <Button
              size="sm"
              loading={providerCancel.isPending}
              onClick={() => providerCancel.mutate(undefined)}
            >
              {t('admin.invoices.cancelAtProvider')}
            </Button>
          )}
          {(pc === 'uncertain' || pc === 'return_required') && (
            <Button size="sm" variant="primary" onClick={() => setDialog('provider')}>
              {pc === 'return_required'
                ? t('admin.invoices.returnDone')
                : t('admin.invoices.resolveProviderCancel')}
            </Button>
          )}
          <HelpTip k="invoices.provider_cancel" />
        </span>
      )}
      {invoice.provider === 'bizimhesap' && (invoice.status === 'draft' || invoice.status === 'failed') && (
        <span className="inline-flex items-center gap-1">
          <Button size="sm" loading={send.isPending} onClick={() => send.mutate(undefined)}>
            {invoice.status === 'failed' ? t('admin.invoices.resend') : t('admin.invoices.send')}
          </Button>
          <HelpTip k="invoices.send" />
        </span>
      )}
      {(invoice.status === 'uncertain' || invoice.status === 'sending') && (
        <span className="inline-flex items-center gap-1">
          <Button size="sm" variant="primary" onClick={() => setDialog('resolve')}>
            {t('admin.invoices.resolve')}
          </Button>
          <HelpTip k="invoices.uncertain" />
        </span>
      )}
      {invoice.status !== 'cancelled' && invoice.status !== 'sending' && (
        <Button size="sm" variant="ghost" onClick={() => setDialog('cancel')}>
          {t('admin.invoices.markCancelled')}
        </Button>
      )}
      {dialog === 'provider' && (
        <ProviderCancelDialog
          invoiceId={invoice.id}
          returnRequired={pc === 'return_required'}
          invalidate={invalidate}
          onClose={() => setDialog(null)}
        />
      )}
      {(dialog === 'resolve' || dialog === 'cancel') && (
        <InvoiceDecisionDialog
          kind={dialog}
          invoiceId={invoice.id}
          invalidate={invalidate}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}

/** BizimHesap iptalini işaretle (F7-25): belirsiz iptalde sonuç, e-belgede iade faturası kesildi. */
function ProviderCancelDialog({
  invoiceId,
  returnRequired,
  invalidate,
  onClose,
}: {
  invoiceId: string;
  returnRequired: boolean;
  invalidate: QueryKey[];
  onClose: () => void;
}) {
  const t = useT();
  const [outcome, setOutcome] = useState<'done' | 'not_done'>('done');
  const [note, setNote] = useState('');
  const m = useApiMutation(
    () => api.post(`/v1/admin/invoices/${invoiceId}/provider-cancel/resolve`, { outcome, note }),
    { invalidate, success: t('common.saved'), onSuccess: onClose, silentFieldErrors: true },
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={returnRequired ? t('admin.invoices.returnDone') : t('admin.invoices.resolveProviderCancel')}
      description={returnRequired ? t('admin.invoices.returnLead') : t('admin.invoices.providerResolveLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={m.isPending} onClick={() => m.mutate(undefined)}>
            {t('admin.confirm.yes')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {!returnRequired && (
          <Field label={t('admin.invoices.outcome')} help={<HelpTip k="invoices.provider_cancel_resolve" />}>
            {(p) => (
              <Select {...p} value={outcome} onChange={(e) => setOutcome(e.target.value as typeof outcome)}>
                <option value="done">{t('admin.invoices.providerOutcomes.done')}</option>
                <option value="not_done">{t('admin.invoices.providerOutcomes.not_done')}</option>
              </Select>
            )}
          </Field>
        )}
        <Field label={t('admin.invoices.note')} required error={fieldError(m.error, 'note')}>
          {(p) => (
            <Textarea {...p} className="min-h-16" value={note} onChange={(e) => setNote(e.target.value)} />
          )}
        </Field>
        {m.error && !m.error.errors.length && (
          <p role="alert" className="text-sm text-rose">
            {m.error.title}
          </p>
        )}
      </div>
    </Dialog>
  );
}

function InvoiceDecisionDialog({
  kind,
  invoiceId,
  invalidate,
  onClose,
}: {
  kind: 'resolve' | 'cancel';
  invoiceId: string;
  invalidate: QueryKey[];
  onClose: () => void;
}) {
  const t = useT();
  const [outcome, setOutcome] = useState<'sent' | 'not_sent'>('sent');
  const [number, setNumber] = useState('');
  const [note, setNote] = useState('');
  const m = useApiMutation(
    () =>
      kind === 'resolve'
        ? api.post(`/v1/admin/invoices/${invoiceId}/resolve`, {
            outcome,
            number: number.trim() || null,
            note,
          })
        : api.post(`/v1/admin/invoices/${invoiceId}/cancel`, { note }),
    { invalidate, success: t('common.saved'), onSuccess: onClose, silentFieldErrors: true },
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={kind === 'resolve' ? t('admin.invoices.resolveTitle') : t('admin.invoices.cancelTitle')}
      description={kind === 'resolve' ? t('admin.invoices.resolveLead') : t('admin.invoices.cancelLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant={kind === 'cancel' ? 'danger' : 'primary'}
            loading={m.isPending}
            onClick={() => m.mutate(undefined)}
          >
            {t('admin.confirm.yes')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {kind === 'resolve' && (
          <>
            <Field label={t('admin.invoices.outcome')}>
              {(p) => (
                <Select {...p} value={outcome} onChange={(e) => setOutcome(e.target.value as typeof outcome)}>
                  <option value="sent">{t('admin.invoices.outcomes.sent')}</option>
                  <option value="not_sent">{t('admin.invoices.outcomes.not_sent')}</option>
                </Select>
              )}
            </Field>
            {outcome === 'sent' && (
              <Field label={t('admin.invoices.number')} optionalLabel={t('common.optional')}>
                {(p) => <Input {...p} value={number} onChange={(e) => setNumber(e.target.value)} />}
              </Field>
            )}
          </>
        )}
        <Field label={t('admin.invoices.note')} required error={fieldError(m.error, 'note')}>
          {(p) => (
            <Textarea {...p} className="min-h-16" value={note} onChange={(e) => setNote(e.target.value)} />
          )}
        </Field>
        {m.error && !m.error.errors.length && (
          <p role="alert" className="text-sm text-rose">
            {m.error.title}
          </p>
        )}
      </div>
    </Dialog>
  );
}

export function InvoicesPage() {
  const t = useT();
  const { formatDate } = useI18n();
  const [status, setStatus] = useState('');
  const q = useQuery({
    queryKey: ['invoices', status],
    queryFn: () => api.get<InvoiceRow[]>(`/v1/admin/invoices${status ? `?status=${status}` : ''}`),
  });
  return (
    <PageFrame helpKey="page.invoices">
      <p className="rounded-sm border border-line bg-surface-2 px-3 py-2 text-sm text-ink-2">
        {t('admin.invoices.bizimhesapNote')}
      </p>
      <Field label={t('admin.invoices.status')} className="max-w-xs">
        {(p) => (
          <Select {...p} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">{t('common.all')}</option>
            {Object.keys(invoiceTone).map((s) => (
              <option key={s} value={s}>
                {t(`admin.invoices.statuses.${s}`)}
              </option>
            ))}
          </Select>
        )}
      </Field>
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : !q.data.length ? (
        <EmptyState title={t('admin.invoices.empty')} description={t('admin.invoices.emptyLead')} />
      ) : (
        <ul className="divide-y divide-line rounded-md border border-line bg-surface">
          {q.data.map((i) => (
            <li key={i.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="font-medium">
                  <Link to="/siparisler/$id" params={{ id: i.orderId }} className="text-pine hover:underline">
                    {i.orderNumber}
                  </Link>
                  <span className="font-normal text-ink-3">
                    {' '}
                    · {t(`admin.invoices.providers.${i.provider}`)}
                    {i.number ? ` · ${i.number}` : ''}
                  </span>
                </p>
                <p className="text-sm text-ink-3">
                  {i.sentAt ? formatDate(i.sentAt, true) : t(`admin.invoices.kinds.${i.kind}`)}
                  {i.error ? ` · ${i.error}` : ''}
                </p>
              </div>
              <Badge tone={invoiceTone[i.status]}>{t(`admin.invoices.statuses.${i.status}`)}</Badge>
              {i.providerUrl && (
                <a
                  href={i.providerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-pine hover:underline"
                >
                  {t('admin.invoices.open')}
                </a>
              )}
              <InvoiceActions invoice={i} invalidate={[['invoices']]} />
            </li>
          ))}
        </ul>
      )}
    </PageFrame>
  );
}
